import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmbeddedInfiniteScrollList } from '@/components/ui/embedded-infinite-scroll-list';
import { CredentialCardView } from '@/components/secrets/CredentialCardView';
import { DashboardWebsiteCredentialsCard } from '@/components/ipx-credentials/DashboardWebsiteCredentialsCard';
import { useAuthStore } from '@/store/auth';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { authApi, paymentsApi, sambaConfigApi } from '@/lib/pillarApi';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { formatAllowedWebsiteTemplateSystemName } from '@/app/allowed-website-templates/allowedWebsiteTemplateSystemDisplay';
import {
    type ActiveRoleAssignmentResponse,
    type AccessibleTerminalInfoListItem,
    type AllowedWebsite,
    type CardPaymentTerminalMenuItem,
    CredentialType,
    type SelectableTerminalAccessFailureReason,
    type SupportedIpgSystemId,
    SupportedIpgSystemIds,
    type UserProfileResponse,
} from '@pillar-portal/api-types';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';

const PAYMENT_DEBUG_PAGE_SIZE: number = 25;
export const DashboardOpaqueCursorSchema: z.ZodType<string> = z.string().trim().min(1);

function formatFeeValue(value: number | null, currency: boolean): string {
    if (value === null) {
        return 'not allowed';
    }
    if (currency) {
        return `$${value.toFixed(2)}`;
    }
    return `${value}%`;
}

function getDashboardTerminalDisplayLabel(
    terminal: Readonly<{ display_label: string }>
): string {
    return terminal.display_label;
}

type DashboardTerminalFeeFields = Pick<
    AccessibleTerminalInfoListItem,
    | 'effective_surcharge_pct'
    | 'effective_surcharge_defined_by'
    | 'effective_convenience_fee'
    | 'effective_convenience_fee_defined_by'
    | 'logical_terminal_uuid'
    | 'processor_tid'
    | 'merchant_account_uuid'
    | 'processor_mid'
>;

function renderTerminalFeeMetadata(
    terminal: Readonly<DashboardTerminalFeeFields>
): ReactNode {
    return (
        <>
            <div className="text-xs text-muted-foreground">
                Effective surcharge:{' '}
                {formatFeeValue(terminal.effective_surcharge_pct, false)} defined by{' '}
                {renderFeeDefinedByText(
                    terminal.effective_surcharge_defined_by,
                    terminal.logical_terminal_uuid,
                    terminal.processor_tid,
                    terminal.merchant_account_uuid,
                    terminal.processor_mid
                )}
            </div>
            <div className="text-xs text-muted-foreground">
                Effective convenience fee:{' '}
                {formatFeeValue(terminal.effective_convenience_fee, true)} defined by{' '}
                {renderFeeDefinedByText(
                    terminal.effective_convenience_fee_defined_by,
                    terminal.logical_terminal_uuid,
                    terminal.processor_tid,
                    terminal.merchant_account_uuid,
                    terminal.processor_mid
                )}
            </div>
        </>
    );
}

type DashboardTerminalQaFields = Pick<
    AccessibleTerminalInfoListItem,
    | 'qa_has_credential_link'
    | 'qa_terminal_device_credential_type_set'
    | 'qa_terminal_device_credential_type_matches_linked_credential_type'
    | 'qa_terminal_virtuality_matches_linked_credential_type'
>;

function renderTerminalQaMetadata(
    terminal: Readonly<DashboardTerminalQaFields>
): ReactNode {
    return (
        <>
            {formatQaCheck(
                'QA: terminal credential link is present:',
                terminal.qa_has_credential_link
            )}
            {formatQaCheck(
                'QA: terminal credential type is set:',
                terminal.qa_terminal_device_credential_type_set
            )}
            {formatQaCheck(
                'QA: terminal credential type matches linked credential:',
                terminal.qa_terminal_device_credential_type_matches_linked_credential_type
            )}
            {formatQaCheck(
                'QA: terminal is_virtual matches linked credential type:',
                terminal.qa_terminal_virtuality_matches_linked_credential_type
            )}
        </>
    );
}

function renderSelectableTerminalCard(
    terminal: CardPaymentTerminalMenuItem
): ReactNode {
    return (
        <CredentialCardView
            credential={{
                account_id: getDashboardTerminalDisplayLabel(terminal),
                system_id: terminal.system_id,
                credential_type: terminal.credential_type,
                status: terminal.credential_is_active ? 'ACTIVE' : 'DISABLED',
                applies_to_children: terminal.credential_applies_to_children,
                public_fields: {},
            }}
            showPublicFields={false}
            titleHref={`/payments-infrastructure/terminal-devices/${terminal.terminal_device_uuid}`}
            className="space-y-2"
        >
            {renderTerminalFeeMetadata(terminal)}
        </CredentialCardView>
    );
}

function renderNavigateMenuWebsiteCard(website: AllowedWebsite): ReactNode {
    return (
        <div className="rounded border p-3 space-y-1">
            <div className="font-medium">{website.name}</div>
            <a
                className="text-sm text-primary underline break-all"
                href={website.url}
                target="_blank"
                rel="noreferrer"
            >
                {website.url}
            </a>
            <div className="text-xs text-muted-foreground">
                {formatAllowedWebsiteTemplateSystemName(website.system_id)}
            </div>
        </div>
    );
}

function renderConfiguredTerminalCard(params: {
    readonly terminal: AccessibleTerminalInfoListItem;
    readonly systemId: SupportedIpgSystemId;
    readonly credentialType: CredentialType;
}): ReactNode {
    const { terminal, systemId, credentialType } = params;
    return (
        <CredentialCardView
            credential={{
                account_id: getDashboardTerminalDisplayLabel(terminal),
                system_id: systemId,
                credential_type: credentialType,
                status: 'ACTIVE',
                applies_to_children: terminal.effective_applies_to_children,
                public_fields: {},
            }}
            showPublicFields={false}
            titleHref={`/payments-infrastructure/terminal-devices/${terminal.terminal_device_uuid}`}
            className="space-y-2"
        >
            {renderTerminalFeeMetadata(terminal)}
            {renderTerminalQaMetadata(terminal)}
        </CredentialCardView>
    );
}

function renderFeeDefinedByText(
    definedBy: string,
    logicalTerminalReference: string | number,
    processorTid: string,
    merchantAccountReference: string | number,
    processorMid: string
): ReactNode {
    if (definedBy === 'terminal_device') {
        return 'this terminal device';
    }
    if (definedBy === 'logical_terminal') {
        return (
            <>
                logical terminal{' '}
                <a
                    className="text-primary underline"
                    href={`/payments-infrastructure/logical-terminals/${logicalTerminalReference}`}
                >
                    {processorTid}
                </a>
            </>
        );
    }
    if (definedBy === 'merchant_account') {
        return (
            <>
                merchant account{' '}
                <a
                    className="text-primary underline"
                    href={`/payments-infrastructure/merchant-accounts/${merchantAccountReference}`}
                >
                    {processorMid}
                </a>
            </>
        );
    }
    return 'not allowed';
}

function formatQaCheck(label: string, pass: boolean): ReactNode {
    return (
        <div className="text-xs text-muted-foreground">
            {label} <span className={pass ? 'text-emerald-600' : 'text-destructive'}>{pass ? 'PASS' : 'FAIL'}</span>
        </div>
    );
}

function parseDashboardCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    const parseResult = DashboardOpaqueCursorSchema.safeParse(pageParam);
    if (!parseResult.success) {
        throw new Error('Expected opaque cursor');
    }
    return parseResult.data;
}

function formatSelectableTerminalEmptyReason(
    reason: SelectableTerminalAccessFailureReason | null
): string | null {
    if (reason === null) {
        return null;
    }
    if (reason === 'no_terminal_devices_configured') {
        return 'No active E-Invoice terminal devices are configured for this merchant.';
    }
    if (reason === 'no_linked_credentials_configured') {
        return 'Active E-Invoice terminal devices exist, but none has a linked credential.';
    }
    if (reason === 'missing_payment_request_create_permission') {
        return (
            'Matching inventory exists, but this user lacks CREATE permission ' +
            'on payment_request scoped to the virtual terminal or wildcard.'
        );
    }
    if (reason === 'missing_credential_permission') {
        return 'Matching inventory exists, but this user lacks permission to use any linked credential.';
    }
    if (reason === 'missing_terminal_permission') {
        return 'Matching inventory exists, but this user lacks permission to use any E-Invoice terminal.';
    }
    return 'Matching inventory exists, but this user lacks both credential and terminal permissions.';
}

type DashboardInfinitePanelProps<TListItem> = {
    title: string;
    queryKeyPrefix: readonly unknown[];
    enabled: boolean;
    fetchPage: (params: {
        readonly limit: number;
        readonly cursor?: string;
    }) => Promise<{
        readonly items: ReadonlyArray<TListItem>;
        readonly pagination: {
            readonly has_more: boolean;
            readonly next_cursor?: string;
        };
    }>;
    renderItem: (item: TListItem, index: number) => ReactNode;
    emptyLabel?: string;
};

function DashboardInfinitePanel<TListItem>({
    title,
    queryKeyPrefix,
    enabled,
    fetchPage,
    renderItem,
    emptyLabel = 'No items.',
}: DashboardInfinitePanelProps<TListItem>) {
    return (
        <div className="space-y-2">
            <div className="font-semibold">{title}</div>
            <div className="rounded border p-2">
                <EmbeddedInfiniteScrollList<TListItem, string, readonly unknown[]>
                    queryKeyPrefix={queryKeyPrefix}
                    pageSize={PAYMENT_DEBUG_PAGE_SIZE}
                    maxHeight="30rem"
                    enabled={enabled}
                    fetchPage={async ({ limit, cursor }) => {
                        return await fetchPage({
                            limit,
                            cursor,
                        });
                    }}
                    parseCursorParam={parseDashboardCursorParam}
                    stateCopy={{
                        loadingLabel: 'Loading...',
                        emptyLabel,
                        endOfListLabel: 'End of list.',
                    }}
                    renderItems={({ items }) => (
                        <div className="space-y-1">
                            {items.map((item, index) => (
                                <div key={index} className="text-sm">
                                    {renderItem(item, index)}
                                </div>
                            ))}
                        </div>
                    )}
                />
            </div>
        </div>
    );
}

function DashboardContent() {
    const { user } = useAuthStore();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const [
        eInvoiceTerminalEmptyReason,
        setEInvoiceTerminalEmptyReason,
    ] = useState<SelectableTerminalAccessFailureReason | null>(null);
    const sharedGatewaySystemId: SupportedIpgSystemId = SupportedIpgSystemIds[0];
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });
    const impersonationContextQuery = useQuery({
        queryKey: ['impersonationContext'],
        queryFn: async () => await authApi.getImpersonationContext(),
        enabled: user !== null,
        staleTime: 5000,
    });
    const shouldShowPaymentDebugCards: boolean =
        activeRoleAssignmentQuery.data?.organization_is_merchant === true;
    const impersonationContext = impersonationContextQuery.data ?? null;
    const navigateMenuWebsitesQuery = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'dashboard',
            'navigate-menu-websites',
        ]),
        queryFn: async () => await sambaConfigApi.getMyNavigateMenuWebsites(),
        enabled: shouldShowPaymentDebugCards,
        staleTime: 30000,
    });

    const getWelcomeName = (
        userData: Pick<UserProfileResponse, 'first_name' | 'last_name' | 'primary_email'>
    ): string => {
        if (impersonationContext !== null) {
            return impersonationContext.impersonated_primary_email;
        }
        const fullName = [userData.first_name, userData.last_name]
            .filter((part): part is string => part !== null && part.trim().length > 0)
            .join(' ')
            .trim();
        if (fullName.length > 0) {
            return fullName;
        }
        return userData.primary_email;
    };

    const activeOrganizationName: string =
        impersonationContext?.impersonated_organization_name ??
        activeRoleAssignmentQuery.data?.organization_name ??
        'Platform';
    const activeRoleName: string =
        impersonationContext?.impersonated_role ??
        activeRoleAssignmentQuery.data?.role ??
        'Unknown';

    return (
        <div>
            <h1 className="text-3xl font-bold mb-8">Dashboard</h1>
            {user && (
                <>
                <Card className="max-w-2xl">
                    <CardHeader>
                        <CardTitle>Welcome, {getWelcomeName(user)}!</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <div>
                            <span className="font-semibold">Active Organization:</span>{' '}
                            {activeOrganizationName}
                        </div>
                        <div>
                            <span className="font-semibold">Role:</span> {activeRoleName}
                        </div>
                        <div className="flex items-center gap-3">
                            <span>
                                <span className="font-semibold">TOTP Enabled:</span>{' '}
                                {user.totp_enabled ? 'Yes' : 'No'}
                            </span>
                            {!user.totp_enabled ? (
                                <Button asChild variant="link" className="h-auto p-0">
                                    <Link to="/profile/security">Set up TOTP</Link>
                                </Button>
                            ) : null}
                        </div>
                    </CardContent>
                </Card>
                {activeRoleAssignmentQuery.data?.organization_uuid !== null &&
                activeRoleAssignmentQuery.data?.organization_uuid !== undefined ? (
                    <DashboardWebsiteCredentialsCard
                        organizationUuid={
                            activeRoleAssignmentQuery.data.organization_uuid
                        }
                    />
                ) : null}
                {shouldShowPaymentDebugCards ? (
                    <Card className="mt-6 max-w-4xl">
                        <CardHeader>
                            <CardTitle>Navigate Menu Websites</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {navigateMenuWebsitesQuery.isLoading ? (
                                <div className="text-sm text-muted-foreground">
                                    Loading...
                                </div>
                            ) : navigateMenuWebsitesQuery.data?.websites.length ? (
                                <div className="grid gap-3 md:grid-cols-2">
                                    {navigateMenuWebsitesQuery.data.websites.map(
                                        (website) => (
                                            <div
                                                key={`${website.type}:${website.system_id}:${website.name}:${website.url}`}
                                            >
                                                {renderNavigateMenuWebsiteCard(website)}
                                            </div>
                                        )
                                    )}
                                </div>
                            ) : (
                                <div className="text-sm text-muted-foreground">
                                    No websites.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ) : null}
                {shouldShowPaymentDebugCards && (
                <div className="mt-6 space-y-4 max-w-4xl">
                    <h2 className="text-2xl font-semibold">Payments Infrastructure Inventory</h2>
                    <Card>
                        <CardHeader>
                            <CardTitle>Configured Gateway Credentials</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <DashboardInfinitePanel
                                title="Virtual Terminal Credentials:"
                                queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                    'dashboard',
                                    'info',
                                    'accessible-credentials',
                                    'logical-terminal',
                                ])}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.getAccessibleCredentialsInfo({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: true,
                                        credential_type: CredentialType.VIRTUAL_TERMINAL,
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.credentials,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(credential) => (
                                    <CredentialCardView
                                        credential={credential}
                                        showPublicFields={true}
                                    />
                                )}
                            />
                            <DashboardInfinitePanel
                                title="Physical Terminal Credentials:"
                                queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                    'dashboard',
                                    'info',
                                    'accessible-credentials',
                                    'terminal-device',
                                ])}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.getAccessibleCredentialsInfo({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: false,
                                        credential_type: CredentialType.PHYSICAL_TERMINAL,
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.credentials,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(credential) => (
                                    <CredentialCardView
                                        credential={credential}
                                        showPublicFields={true}
                                    />
                                )}
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Configured Terminals</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <DashboardInfinitePanel
                                title="Physical terminal access configurations:"
                                queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                    'dashboard',
                                    'info',
                                    'accessible-terminals',
                                    'physical',
                                ])}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.getAccessibleTerminalsInfo({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: false,
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(terminal) =>
                                    renderConfiguredTerminalCard({
                                        terminal,
                                        systemId: sharedGatewaySystemId,
                                        credentialType: CredentialType.PHYSICAL_TERMINAL,
                                    })
                                }
                            />
                            <DashboardInfinitePanel
                                title="Virtual terminal access configurations:"
                                queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                    'dashboard',
                                    'info',
                                    'accessible-terminals',
                                    'virtual',
                                ])}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.getAccessibleTerminalsInfo({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: true,
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(terminal) =>
                                    renderConfiguredTerminalCard({
                                        terminal,
                                        systemId: sharedGatewaySystemId,
                                        credentialType: CredentialType.VIRTUAL_TERMINAL,
                                    })
                                }
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Available Terminal Selections</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                The following payment terminals are available to you in the Anchorbase app
                                based on the configuration above.
                            </p>
                            <DashboardInfinitePanel
                                title="Physical terminals:"
                                queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                    'dashboard',
                                    'info',
                                    'joined-terminals',
                                    'physical',
                                ])}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.listCardPaymentTerminalDevices({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: false,
                                        action: 'TAKE_PAYMENT',
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={renderSelectableTerminalCard}
                            />
                            <DashboardInfinitePanel
                                title="Virtual terminals:"
                                queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                    'dashboard',
                                    'info',
                                    'joined-terminals',
                                    'virtual',
                                ])}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.listCardPaymentTerminalDevices({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: true,
                                        action: 'TAKE_PAYMENT',
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={renderSelectableTerminalCard}
                            />
                            <DashboardInfinitePanel
                                title="E-Invoice terminals:"
                                queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                    'dashboard',
                                    'info',
                                    'joined-terminals',
                                    'e-invoice',
                                ])}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.listCardPaymentTerminalDevices({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: true,
                                        action: 'CREATE',
                                        resource_type: 'payment_request',
                                        include_empty_reason: true,
                                        limit,
                                        cursor,
                                    });
                                    if (cursor === undefined) {
                                        setEInvoiceTerminalEmptyReason(
                                            response.empty_reason ?? null
                                        );
                                    }
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={renderSelectableTerminalCard}
                                emptyLabel={
                                    formatSelectableTerminalEmptyReason(
                                        eInvoiceTerminalEmptyReason
                                    ) ?? 'No items.'
                                }
                            />
                        </CardContent>
                    </Card>
                </div>
                )}
                </>
            )}
        </div>
    );
}

export default function DashboardPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <DashboardContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
